import React from "react"
import { Grid, TextField, FormLabel } from "@material-ui/core"

import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"
import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"

import { ChatBotFlowBlockRule } from "@/protocols/chatBot"

import useConditionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useConditionEditor"

import { consolidateAIConditionMatchDefaultValidationIfNeeded, DEFAULT_AI_CONDITION_VALIDATION_INDEX } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockRule"

type AIConditionSectionEditorProps = {
	onSave: (chatBotFlowBlockRule: ChatBotFlowBlockRule) => void
	onDelete?: () => void
	chatBotFlowBlockRule: ChatBotFlowBlockRule
	hideChildSelection?: boolean
}

const DEFAULT_PRIMARY_CONDITION_SELECT_NAME = "Mensagem recebida"
const DEFAULT_SECONDARY_CONDITION_SELECT_NAME = "Representa"

const AIConditionSectionEditor: React.FC<AIConditionSectionEditorProps> = (props) => {
	const {
		onSave,
		chatBotFlowBlockRule,
		children,
		hideChildSelection,
		onDelete
	} = props

	const conditionEditor = useConditionEditor({ chatBotFlowBlockRule })

	const validation = conditionEditor.getValidation(DEFAULT_AI_CONDITION_VALIDATION_INDEX)

	const handleSave = (chatBotFlowBlockRule: ChatBotFlowBlockRule) => {
		const consolidatedRule = consolidateAIConditionMatchDefaultValidationIfNeeded(chatBotFlowBlockRule)

		onSave(consolidatedRule)
	}

	return (
		<BlockBodySectionEditor
			title="CONDIÇÃO"
			onSave={() => handleSave(conditionEditor.rule)}
			onOpen={conditionEditor.reset}
			onDelete={onDelete}
			hideChildSelection={hideChildSelection}
			AsideDrawerProps={{
				children: (
					<Grid
						container
						spacing={3}
					>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={12}
								>
									<FormLabel>
										<b>Qual é a condição?</b>
									</FormLabel>
								</Grid>
								<Grid
									item
									xs={12}
								>
									<Grid
										container
										spacing={1}
									>
										<Grid
											item
											xs={12}
										>
											<SelectInput
												multiple={false}
												defaultOption={{ name: DEFAULT_PRIMARY_CONDITION_SELECT_NAME }}
												value={{ name: DEFAULT_PRIMARY_CONDITION_SELECT_NAME }}
												onChange={() => Promise.resolve(null)}
												options={[{ name: DEFAULT_PRIMARY_CONDITION_SELECT_NAME }]}
											/>
										</Grid>
										<Grid
											item
											xs={12}
										>
											<SelectInput
												multiple={false}
												defaultOption={{ name: DEFAULT_SECONDARY_CONDITION_SELECT_NAME }}
												value={{ name: DEFAULT_SECONDARY_CONDITION_SELECT_NAME }}
												onChange={() => Promise.resolve(null)}
												options={[{ name: DEFAULT_SECONDARY_CONDITION_SELECT_NAME }]}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={12}
								>
									<FormLabel>
										<b>Qual é o título dessa condição?</b>
									</FormLabel>
								</Grid>
								<Grid
									item
									xs={12}
								>
									<TextField
										value={validation.extra_data?.ai_condition?.title}
										defaultValue={validation.extra_data?.ai_condition?.title}
										onChange={({ target }) => (
											conditionEditor.changeValidation(DEFAULT_AI_CONDITION_VALIDATION_INDEX, {
												extra_data: {
													ai_condition: {
														title: target.value
													}
												}
											})
										)}
										variant="outlined"
										color="primary"
										fullWidth
										placeholder="Nome e sobrenome"
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={12}
								>
									<FormLabel>
										<b>Como a condição deve ser identificada?</b>
									</FormLabel>
								</Grid>
								<Grid
									item
									xs={12}
								>
									<TextField
										value={validation.extra_data?.ai_condition?.description}
										defaultValue={validation.extra_data?.ai_condition?.description}
										onChange={({ target }) => (
											conditionEditor.changeValidation(DEFAULT_AI_CONDITION_VALIDATION_INDEX, {
												extra_data: {
													ai_condition: {
														description: target.value
													}
												}
											})
										)}
										variant="outlined"
										color="primary"
										fullWidth
										minRows={3}
										multiline
										placeholder="Se o contato enviou exatamente um nome e sobrenome."
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={12}
								>
									<FormLabel>
										<b>Quais exemplos representam essa condição?</b>
									</FormLabel>
								</Grid>
								<Grid
									item
									xs={12}
								>
									<TextField
										value={validation.extra_data?.ai_condition?.example}
										defaultValue={validation.extra_data?.ai_condition?.example}
										onChange={({ target }) => (
											conditionEditor.changeValidation(DEFAULT_AI_CONDITION_VALIDATION_INDEX, {
												extra_data: {
													ai_condition: {
														example: target.value
													}
												}
											})
										)}
										variant="outlined"
										color="primary"
										fullWidth
										minRows={3}
										multiline
										placeholder="'João Silva', 'Maria Oliveira'."
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
			DeletePopConfirmProps={{
				title: "Excluir condição",
				description: "Tem certeza? Essa ação é irreversível.",
				confirmButtonText: "EXCLUIR"
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default AIConditionSectionEditor
